// src/pages/EmploymentViewPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Card, Col, Row, Spinner, Table, Badge } from "react-bootstrap";
import api from "../api";
import ClientContactLine from "../components/ClientContactLine";

function displayOrDash(v) {
  const s = v == null ? "" : String(v).trim();
  return s ? s : "—";
}

function fmtMoneyOrDash(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return displayOrDash(v);
  return n.toFixed(2);
}

function fmtNumberOrDash(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return displayOrDash(v);
  return n.toFixed(2);
}

function personRoleLabel(v) {
  return v === "spouse" ? "Spouse" : "Client";
}

function payTypeLabel(v) {
  // keep as-is if backend returns something else
  if (!v) return "—";
  if (v === "hourly") return "Hourly";
  if (v === "salary") return "Salary";
  return v;
}

export default function EmploymentViewPage() {
  const { id } = useParams(); // client id
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);

  useEffect(() => {
    let isMounted = true;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const res = await api.get(`/clients/${id}/employment`);
        const data = res?.data || {};
        if (!isMounted) return;
        setItems(Array.isArray(data.items) ? data.items : []);
      } catch (e) {
        // mirror edit behavior: don't block UI if endpoint missing
        if (!isMounted) return;
        setItems([]);
        // keep this non-blocking; only show error if you want
        // setError(e?.response?.data?.error || e?.message || "Failed to load employment.");
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    load();
    return () => {
      isMounted = false;
    };
  }, [id]);

  const hasItems = items.length > 0;

  const currentCount = useMemo(
    () => items.filter((x) => x?.is_current).length,
    [items]
  );

  if (loading) {
    return (
      <div className="p-3">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading employment...
      </div>
    );
  }

  return (
    <div className="p-3">
      <Row className="mb-3">
        <Col>
          <h3 className="mb-0">Employment</h3>
          <ClientContactLine clientId={id} />
          <div className="text-muted">Client ID: {id}</div>
          {hasItems ? (
            <div className="text-muted mt-1" style={{ fontSize: "0.95rem" }}>
              {items.length} record{items.length === 1 ? "" : "s"}{" "}
              {currentCount ? (
                <>
                  • <Badge bg="success">{currentCount} current</Badge>
                </>
              ) : null}
            </div>
          ) : null}
        </Col>

        <Col className="d-flex justify-content-end align-items-start gap-2">
          <Button
            variant="outline-secondary"
            type="button"
            onClick={() => navigate("/clients")}
          >
            Back to Clients
          </Button>

          {/* Edit Employment takes them to the existing EDIT tab route */}
          <Button
            variant="primary"
            type="button"
            onClick={() => navigate(`/clients/${id}/employment`)}
          >
            Edit Employment
          </Button>
        </Col>
      </Row>

      {error ? (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      ) : null}

      <Card className="mb-3">
        <Card.Header>Employment Records</Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Person</th>
                <th>Employer</th>
                <th>Job Title</th>
                <th style={{ width: 110 }}>Pay Type</th>
                <th style={{ width: 120 }}>Pay Rate</th>
                <th style={{ width: 120 }}>Hours/Week</th>
                <th style={{ width: 90 }}>Current</th>
              </tr>
            </thead>

            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-muted">
                    No employment records yet.
                  </td>
                </tr>
              ) : (
                items.map((x) => (
                  <tr key={x.id}>
                    <td>{personRoleLabel(x.person_role)}</td>
                    <td>{displayOrDash(x.employer_name)}</td>
                    <td>{displayOrDash(x.job_title)}</td>
                    <td>{payTypeLabel(x.pay_type)}</td>
                    <td>{x.pay_rate != null ? fmtMoneyOrDash(x.pay_rate) : "—"}</td>
                    <td>{x.hours_per_week != null ? fmtNumberOrDash(x.hours_per_week) : "—"}</td>
                    <td>{x.is_current ? "Yes" : "No"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Optional: detail cards under the table (commented out).
          If you want, we can expand each record to show supervisor/how-long, etc. */}
      {/* {items.map((x) => (
        <Card className="mb-2" key={`detail-${x.id}`}>
          <Card.Body>
            <Row>
              <Col md={6}>
                <div className="text-muted" style={{ fontSize: "0.9rem" }}>Supervisor</div>
                <div>{displayOrDash(x.supervisor_name)}</div>
              </Col>
              <Col md={6}>
                <div className="text-muted" style={{ fontSize: "0.9rem" }}>Supervisor Phone</div>
                <div>{displayOrDash(x.supervisor_phone)}</div>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md={6}>
                <div className="text-muted" style={{ fontSize: "0.9rem" }}>How Long Employed</div>
                <div>{displayOrDash(x.how_long_employed)}</div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      ))} */}
    </div>
  );
}
